import { HttpError } from '@magicdoorinc/errors';
import { PermissionMatcher } from '@magicdoorinc/rbac';
import { createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createMemo } from 'solid-js';
import { getActivePermissions } from '~/config/auth';
import { useAuth } from '~/contexts/auth/AuthContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { propertyManagerPermissionsRepository } from '~/repositories/propertyManagerPermissionsRepository';
import { PM_MENU_ACCESS_KEYS } from '~/utils/constant';

export const [PropertyManagerPermissionsProvider, usePropertyManagerPermissions] = createMagicDoorContext(
  'PropertyManagerPermissions',
  () => {
    const { me, activeAccountId } = useAuth();
    const permissionMatcher = new PermissionMatcher();

    const [propertyAccessData, { mutate: mutatePropertyAccess, refetch: refetchPropertyAccess }] = createLazyResource(
      () => me()?.id,
      async (managerId) => {
        if (managerId) {
          return await propertyManagerPermissionsRepository.getPropertyAccess(managerId);
        }
        return { allProperties: false, propertyIds: [], portfolioIds: [] };
      }
    );

    const [menuAccessData] = createLazyResource(
      () => me()?.id,
      async (managerId) => {
        if (managerId) {
          try {
            const result = await propertyManagerPermissionsRepository.getMenuAccess(managerId);
            return { ...result, managerId };
          } catch (e) {
            // Fail open only when the endpoint is not deployed yet.
            if (e instanceof HttpError && e.getStatusCode() === 404) {
              return { menuAccess: PM_MENU_ACCESS_KEYS, managerId };
            }

            console.error(e);
            return { menuAccess: [], managerId };
          }
        }
        return undefined;
      }
    );

    const permissionsReady = createMemo(() => !me.loading);

    const menuAccessReady = createMemo(() => {
      const managerId = me()?.id;
      if (me.loading) return false;
      if (!managerId) return true;
      return menuAccessData.latest?.managerId === managerId && !menuAccessData.loading;
    });

    const permissions = createMemo<string[]>(() => {
      activeAccountId();
      return getActivePermissions();
    });
    const propertyAccess = createMemo(() => propertyAccessData.latest);
    const menuAccess = createMemo(() => {
      const managerId = me()?.id;
      if (!managerId || menuAccessData.latest?.managerId !== managerId) {
        return [];
      }
      return menuAccessData.latest.menuAccess || [];
    });

    const hasPermission = (resource: string, action: string): boolean => {
      const permission = `${resource}:${action}`;
      return permissionMatcher.checkPermission(permissions(), permission);
    };

    const addPortfolioAccess = createMutation(async (portfolioId: string) => {
      const managerId = me()?.id;
      if (!managerId) return;

      const currentAccess = propertyAccess();
      if (currentAccess?.allProperties) return;

      await propertyManagerPermissionsRepository.addPropertyAccess(managerId, {
        allProperties: false,
        portfolioId,
      });

      mutatePropertyAccess((prev) => ({
        ...prev,
        allProperties: prev?.allProperties || false,
        portfolioIds: [...(prev?.portfolioIds || []), portfolioId],
        propertyIds: prev?.propertyIds || [],
      }));
    });

    const addPropertyAccess = createMutation(async (propertyId: string) => {
      const managerId = me()?.id;
      if (!managerId) return;

      const currentAccess = propertyAccess();
      if (currentAccess?.allProperties) return;

      await propertyManagerPermissionsRepository.addPropertyAccess(managerId, {
        allProperties: false,
        propertyId,
      });

      mutatePropertyAccess((prev) => ({
        ...prev,
        allProperties: prev?.allProperties || false,
        portfolioIds: prev?.portfolioIds || [],
        propertyIds: [...(prev?.propertyIds || []), propertyId],
      }));
    });

    return {
      permissionsReady,
      permissions,
      propertyAccess,
      menuAccessData,
      menuAccessReady,
      menuAccess,
      hasPermission,
      addPortfolioAccess,
      addPropertyAccess,
      refetchPropertyAccess,
    };
  }
);
